import React, { useEffect, useState } from 'react';
import { PersonCard } from './PersonCard';
import { FilterBar } from './FilterBar';
import { peopleProfiles } from '../utils/mockData';
import { UsersIcon, FilterIcon, Download as DownloadIcon, Share2 as ShareIcon } from 'lucide-react';
import { PersonModal } from './PersonModal';
import { AdvancedFilters } from './AdvancedFilters';
interface PeopleProps {
  onPersonSelect: (personId: number) => void;
  selectedPersonId: number | null;
  onMarkInteresting: (personId: number) => void;
  onMarkNotRelevant: (personId: number) => void;
  interestingPeople: number[];
  notRelevantPeople: number[];
}
export const People: React.FC<PeopleProps> = ({
  onPersonSelect,
  selectedPersonId,
  onMarkInteresting,
  onMarkNotRelevant,
  interestingPeople,
  notRelevantPeople
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('interesting');
  const [showPersonModal, setShowPersonModal] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [sortOrder, setSortOrder] = useState<string>('relevance');
  const [advancedFilters, setAdvancedFilters] = useState<any>({});
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const allTags = Array.from(new Set(peopleProfiles.flatMap(person => person.tags)));
  const handlePersonClick = (personId: number) => {
    onPersonSelect(personId);
    setShowPersonModal(true);
  };
  const handleClosePersonModal = () => {
    setShowPersonModal(false);
  };
  const handleNavigateToMessages = (personId: number) => {
    onPersonSelect(personId);
    // This would normally navigate to messages tab, but we'll handle that in the parent component
  };
  const handleOpenAdvancedFilters = () => {
    setShowAdvancedFilters(true);
  };
  const handleCloseAdvancedFilters = () => {
    setShowAdvancedFilters(false);
  };
  const handleApplyAdvancedFilters = (filters: any) => {
    setAdvancedFilters(filters);
    // Apply the filters to the people list
  };
  const handleExportPeople = () => {
    // This would normally export the filtered people list
    console.log('Exporting people list');
    alert('People list exported successfully!');
  };
  const filteredPeople = peopleProfiles.filter(person => {
    // Filter by search term
    const matchesSearch = searchTerm === '' || person.name.toLowerCase().includes(searchTerm.toLowerCase()) || person.title.toLowerCase().includes(searchTerm.toLowerCase()) || person.company.toLowerCase().includes(searchTerm.toLowerCase());
    // Filter by selected tags
    const matchesTags = selectedTags.length === 0 || selectedTags.some(tag => person.tags.includes(tag));
    // Filter by tab
    const matchesTab = activeTab === 'interesting' && interestingPeople.includes(person.id) || activeTab === 'not-relevant' && notRelevantPeople.includes(person.id) || activeTab === 'all';
    // Apply advanced filters if they exist
    let matchesAdvancedFilters = true;
    if (Object.keys(advancedFilters).length > 0) {
      if (advancedFilters.relevanceLevel?.length > 0) {
        matchesAdvancedFilters = matchesAdvancedFilters && advancedFilters.relevanceLevel.includes(person.relevance);
      }
      if (advancedFilters.engagementType?.length > 0) {
        const personEngagementTypes = person.interactions.map(i => i.type);
        matchesAdvancedFilters = matchesAdvancedFilters && advancedFilters.engagementType.some((type: string) => {
          if (type === 'multiple') {
            return person.interactions.length > 1;
          }
          return personEngagementTypes.includes(type);
        });
      }
    }
    return matchesSearch && matchesTags && matchesTab && matchesAdvancedFilters;
  });
  // Sort the filtered people
  const sortedPeople = [...filteredPeople].sort((a, b) => {
    if (sortOrder === 'relevance') {
      const relevanceOrder = {
        high: 3,
        medium: 2,
        low: 1
      };
      return relevanceOrder[b.relevance as keyof typeof relevanceOrder] - relevanceOrder[a.relevance as keyof typeof relevanceOrder];
    } else if (sortOrder === 'recent') {
      // Sort by most recent interaction
      const getLatestDate = (person: (typeof peopleProfiles)[0]) => {
        if (person.interactions.length === 0) return 0;
        const dates = person.interactions.map(i => {
          if (i.date.includes('day')) {
            return parseInt(i.date) || 0;
          }
          if (i.date.includes('week')) {
            return parseInt(i.date) * 7 || 0;
          }
          return 30; // for "month" or any other unit
        });
        return Math.min(...dates);
      };
      return getLatestDate(a) - getLatestDate(b);
    } else if (sortOrder === 'name') {
      return a.name.localeCompare(b.name);
    }
    return 0;
  });
  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };
  const handleTagSelect = (tag: string) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-643">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3 sm:gap-0" data-id="element-644">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-645">People</h1>
        <div className="flex flex-wrap gap-2" data-id="element-646">
          <button onClick={handleExportPeople} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-647">
            <DownloadIcon size={16} className="mr-2" data-id="element-648" />
            Export
          </button>
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-649">
            <ShareIcon size={16} className="mr-2" data-id="element-650" />
            Share
          </button>
        </div>
      </div>
      <div className="mb-4 overflow-x-auto" data-id="element-651">
        <div className="border-b border-gray-200 min-w-max" data-id="element-652">
          <nav className="-mb-px flex space-x-8" data-id="element-653">
            <button onClick={() => setActiveTab('interesting')} className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'interesting' ? 'border-[#f9b30f] text-[#f9b30f]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-654">
              Interesting ({interestingPeople.length})
            </button>
            <button onClick={() => setActiveTab('not-relevant')} className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'not-relevant' ? 'border-[#f9b30f] text-[#f9b30f]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-655">
              Not Relevant ({notRelevantPeople.length})
            </button>
            <button onClick={() => setActiveTab('all')} className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'all' ? 'border-[#f9b30f] text-[#f9b30f]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-656">
              All Profiles
            </button>
          </nav>
        </div>
      </div>
      <div className="mb-4" data-id="element-657">
        <FilterBar onSearch={handleSearch} selectedTags={selectedTags} onTagSelect={handleTagSelect} availableTags={allTags} data-id="element-658" />
      </div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3" data-id="element-659">
        <div className="text-sm text-gray-500" data-id="element-660">
          {sortedPeople.length} people found
        </div>
        <div className="flex items-center space-x-3" data-id="element-661">
          <div data-id="element-662">
            <label htmlFor="sort" className="sr-only" data-id="element-663">
              Sort by
            </label>
            <select id="sort" name="sort" value={sortOrder} onChange={e => setSortOrder(e.target.value)} className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] sm:text-sm rounded-md" data-id="element-664">
              <option value="relevance" data-id="element-665">Sort by: Relevance</option>
              <option value="recent" data-id="element-666">Sort by: Most Recent</option>
              <option value="name" data-id="element-667">Sort by: Name</option>
            </select>
          </div>
          <button onClick={handleOpenAdvancedFilters} className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-668">
            <FilterIcon size={14} className="mr-1" data-id="element-669" />
            Advanced Filters
          </button>
          <div className="flex border border-gray-300 rounded-md overflow-hidden" data-id="element-670">
            <button onClick={() => setViewMode('grid')} className={`px-3 py-1.5 text-sm font-medium ${viewMode === 'grid' ? 'bg-gray-100' : 'bg-white'}`} data-id="element-671">
              Grid
            </button>
            <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 text-sm font-medium ${viewMode === 'list' ? 'bg-gray-100' : 'bg-white'}`} data-id="element-672">
              List
            </button>
          </div>
        </div>
      </div>
      {Object.keys(advancedFilters).length > 0 && <div className="mb-4 p-3 bg-gray-50 rounded-md border border-gray-200 flex items-center justify-between" data-id="element-673">
          <div className="flex items-center" data-id="element-674">
            <span className="text-sm font-medium text-gray-700 mr-2" data-id="element-675">
              Active filters:
            </span>
            <div className="flex flex-wrap gap-2" data-id="element-676">
              {advancedFilters.relevanceLevel?.length > 0 && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-677">
                  Relevance: {advancedFilters.relevanceLevel.join(', ')}
                </span>}
              {advancedFilters.engagementType?.length > 0 && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-678">
                  Engagement: {advancedFilters.engagementType.join(', ')}
                </span>}
              {advancedFilters.dateRange && advancedFilters.dateRange !== 'all' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-679">
                    Date: {advancedFilters.dateRange}
                  </span>}
            </div>
          </div>
          <button onClick={() => setAdvancedFilters({})} className="text-sm text-gray-600 hover:text-gray-900" data-id="element-680">
            Clear all
          </button>
        </div>}
      {sortedPeople.length > 0 ? viewMode === 'grid' ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-681">
            {sortedPeople.map(person => <PersonCard key={person.id} person={person} onSelect={handlePersonClick} isSelected={selectedPersonId === person.id} onMarkInteresting={onMarkInteresting} onMarkNotRelevant={onMarkNotRelevant} showActions={activeTab !== 'not-relevant'} data-id="element-682" />)}
          </div> : <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden" data-id="element-683">
            {sortedPeople.map(person => <div key={person.id} className={`p-4 bg-white hover:bg-gray-50 cursor-pointer ${selectedPersonId === person.id ? 'border-l-4 border-[#f9b30f]' : ''}`} onClick={() => handlePersonClick(person.id)} data-id="element-684">
                <div className="flex items-center justify-between" data-id="element-685">
                  <div className="flex items-center" data-id="element-686">
                    <img src={person.image} alt={person.name} className="w-10 h-10 rounded-full mr-3" data-id="element-687" />
                    <div data-id="element-688">
                      <div className="font-medium text-gray-900" data-id="element-689">
                        {person.name}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-690">
                        {person.title} at {person.company}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3" data-id="element-691">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${person.relevance === 'high' ? 'bg-green-100 text-green-800' : person.relevance === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-692">
                      {person.relevance.charAt(0).toUpperCase() + person.relevance.slice(1)}{' '}
                      relevance
                    </span>
                    {activeTab !== 'not-relevant' && <div className="flex space-x-2" data-id="element-693">
                        <button onClick={e => {
                e.stopPropagation();
                onMarkNotRelevant(person.id);
              }} className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200" data-id="element-694">
                          Skip
                        </button>
                        <button onClick={e => {
                e.stopPropagation();
                onMarkInteresting(person.id);
              }} className="px-3 py-1 text-xs font-medium text-white bg-[#f9b30f] rounded hover:bg-[#e0a40e]" data-id="element-695">
                          Interesting
                        </button>
                      </div>}
                  </div>
                </div>
              </div>)}
          </div> : <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-id="element-696">
          <UsersIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-697" />
          <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-698">
            No people found
          </h3>
          <p className="text-gray-500" data-id="element-699">
            Try adjusting your filters or search terms.
          </p>
        </div>}
      {showPersonModal && selectedPersonId && <PersonModal personId={selectedPersonId} onClose={handleClosePersonModal} onNavigateToMessages={handleNavigateToMessages} data-id="element-700" />}
      <AdvancedFilters isOpen={showAdvancedFilters} onClose={handleCloseAdvancedFilters} onApplyFilters={handleApplyAdvancedFilters} data-id="element-701" />
    </div>;
};